import { Link } from "react-router-dom";
import { Avatar } from "../../components/Avatar.js";
import {
  buildIndividualStandings,
  buildSwissStandings,
  type IndividualRow,
} from "../../lib/standings.js";
import type { SwissRow } from "@rally/draw";
import type { TournamentEvent } from "../../lib/types.js";
import { GroupTable, useGroupTables } from "./GroupTable.js";
import { EventGate, EventScreen, type Person, type Roster } from "./shared.js";

/**
 * The table — requirements §5.3's, with its tiebreak chain spelled out on the screen rather
 * than left to be discovered in an argument.
 *
 * Computed from the results on the way in, never stored. A standings row is a sum of
 * matches, and a stored sum is wrong from the moment a match is voided until somebody
 * remembers to rebuild it.
 */
export function EventStandings() {
  return <EventGate>{({ event }) => <Standings event={event} />}</EventGate>;
}

function Standings({ event }: { event: TournamentEvent }) {
  const table = useGroupTables(event);
  const { fixtures, roster, isPending, groups } = table;

  if (event.format === "americano" || event.format === "mexicano") {
    return (
      <EventScreen event={event} title="Standings">
        <Individual fixtures={fixtures} roster={roster} pending={isPending} />
      </EventScreen>
    );
  }

  if (event.format === "swiss") {
    return (
      <EventScreen event={event} title="Standings">
        <SwissTable fixtures={fixtures} roster={roster} pending={isPending} />
      </EventScreen>
    );
  }

  if (event.format === "knockout") {
    return (
      <EventScreen event={event} title="Standings">
        <p className="text-sm text-ink-muted">
          A knockout has no table — you are in it until you lose. The draw is the standing.
        </p>
        <Link to={`/events/${event.id}/draw`} className="micro mt-4 inline-block underline">
          The draw
        </Link>
      </EventScreen>
    );
  }

  return (
    <EventScreen event={event} title="Standings">
      {groups.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {isPending ? "Loading…" : "Nothing has been played yet, so there is nothing to rank."}
        </p>
      ) : (
        <>
          {groups.map((group) => (
            <section key={group.pool ?? "all"} className="mb-8">
              {group.pool !== null ? (
                <h2 className="font-mono mb-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
                  Group {group.pool}
                </h2>
              ) : null}
              <GroupTable
                group={group}
                isTie={table.isTie}
                label={table.label}
                faces={table.faces}
                isYours={table.isYours}
              />
            </section>
          ))}

          {/* Spelled out because somebody will argue, and the person who has to answer them
              is the organiser standing next to the net. */}
          <p className="micro" style={{ textTransform: "none", letterSpacing: 0 }}>
            Ordered by matches won, then the matches between whoever is level, then game difference,
            then point difference.
          </p>
        </>
      )}
    </EventScreen>
  );
}

const headerStyle = { borderColor: "var(--color-line)" };

/** One row per player, ranked on points scored. The americano table. */
function Individual({
  fixtures,
  roster,
  pending,
}: {
  fixtures: { result?: unknown }[];
  roster: Roster;
  pending: boolean;
}) {
  const rows = buildIndividualStandings(fixtures as Parameters<typeof buildIndividualStandings>[0]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        {pending ? "Loading…" : "Nothing has been played yet, so there is nothing to rank."}
      </p>
    );
  }

  return (
    <>
      <div className="grid grid-cols-[1fr_auto_auto_auto] items-baseline gap-x-3">
        <span className="micro border-b pb-1" style={headerStyle}>
          Player
        </span>
        <span className="micro border-b pb-1 text-right" style={headerStyle} title="Played">
          P
        </span>
        <span className="micro border-b pb-1 text-right" style={headerStyle} title="Won">
          W
        </span>
        <span className="micro border-b pb-1 text-right" style={headerStyle} title="Points">
          PTS
        </span>

        {rows.map((row, index) => (
          <IndividualLine
            key={row.memberId}
            row={row}
            place={index + 1}
            person={roster.get(row.memberId) ?? { name: "withdrawn", url: null }}
          />
        ))}
      </div>

      <p className="micro mt-6" style={{ textTransform: "none", letterSpacing: 0 }}>
        Every point you were on court for is yours, whoever you were partnering. Ordered by points,
        then matches won, then points conceded.
      </p>
    </>
  );
}

function IndividualLine({
  row,
  place,
  person,
}: {
  row: IndividualRow;
  place: number;
  person: Person;
}) {
  const cell = "font-mono border-b py-2 text-right text-sm";
  const line = { borderColor: "var(--color-line-muted)" };

  return (
    <>
      <span className="flex min-w-0 items-center gap-2 border-b py-2 text-sm" style={line}>
        <span className="micro">{place}</span>
        <Avatar name={person.name} url={person.url} size={22} />
        <span className="truncate">{person.name}</span>
      </span>
      <span className={cell} style={line}>
        {row.played}
      </span>
      <span className={cell} style={line}>
        {row.won}
      </span>
      <span className={cell} style={line}>
        {row.points}
      </span>
    </>
  );
}

/** The Swiss table: wins, then Buchholz, then game and point difference. */
function SwissTable({
  fixtures,
  roster,
  pending,
}: {
  fixtures: Parameters<typeof buildSwissStandings>[0];
  roster: Roster;
  pending: boolean;
}) {
  const rows = buildSwissStandings(fixtures);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        {pending ? "Loading…" : "Nothing has been played yet, so there is nothing to rank."}
      </p>
    );
  }

  const columns = [
    { title: "Played, a bye included", label: "P" },
    { title: "Won, a bye included", label: "W" },
    { title: "Buchholz: the wins of everyone you played", label: "BH" },
    { title: "Game difference", label: "G±" },
    { title: "Point difference", label: "PT±" },
  ];

  return (
    <>
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-baseline gap-x-3">
        <span className="micro border-b pb-1" style={headerStyle}>
          Player
        </span>
        {columns.map((column) => (
          <span
            key={column.label}
            className="micro border-b pb-1 text-right"
            style={headerStyle}
            title={column.title}
          >
            {column.label}
          </span>
        ))}

        {rows.map((row, index) => (
          <SwissLine
            key={row.memberId}
            row={row}
            place={index + 1}
            person={roster.get(row.memberId) ?? { name: "withdrawn", url: null }}
          />
        ))}
      </div>

      {/* Spelled out for the same reason as every other table: somebody will argue. */}
      <p className="micro mt-6" style={{ textTransform: "none", letterSpacing: 0 }}>
        Ordered by matches won, then Buchholz — the wins of everyone you played between them — then
        game difference, then point difference. A bye counts as a win.
      </p>
    </>
  );
}

const signed = (value: number) => (value > 0 ? `+${value}` : String(value));

function SwissLine({ row, place, person }: { row: SwissRow; place: number; person: Person }) {
  const cell = "font-mono border-b py-2 text-right text-sm";
  const line = { borderColor: "var(--color-line-muted)" };

  return (
    <>
      <span className="flex min-w-0 items-center gap-2 border-b py-2 text-sm" style={line}>
        <span className="micro">{place}</span>
        <Avatar name={person.name} url={person.url} size={22} />
        <span className="truncate">{person.name}</span>
      </span>
      <span className={cell} style={line}>
        {row.played}
      </span>
      <span className={cell} style={line}>
        {row.wins}
      </span>
      <span className={cell} style={line}>
        {row.buchholz}
      </span>
      <span className={cell} style={line}>
        {signed(row.gameDiff)}
      </span>
      <span className={cell} style={line}>
        {signed(row.pointDiff)}
      </span>
    </>
  );
}
